import * as React from "react";
import { Search, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input, type InputProps } from "./input";

export type SearchInputProps = Omit<InputProps, "type"> & {
  /** Show a clear button when input has value */
  clearable?: boolean;
  /** Callback when clear button is clicked */
  onClear?: () => void;
  /** Container className for the wrapper div */
  containerClassName?: string;
};

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      containerClassName,
      clearable = true,
      onClear,
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    const hasValue = value && String(value).length > 0;

    const handleClear = () => {
      if (onClear) {
        onClear();
      } else if (onChange) {
        // Simulate a change event with empty value
        const event = {
          target: { value: "" },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
    };

    return (
      <div className={cn("relative", containerClassName)}>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          ref={ref}
          type="search"
          value={value}
          onChange={onChange}
          className={cn("pl-9", clearable && hasValue && "pr-9", className)}
          {...props}
        />
        {clearable && hasValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  },
);
SearchInput.displayName = "SearchInput";
